import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useCreateTaskComment,
  useCreateTimeEntry,
  useDeleteComment,
  useMe,
  useTask,
  useTaskActivity,
  useTaskComments,
  useTaskPullRequests,
  useTaskTimeEntries,
  useUpdateComment,
  useUpdateTask,
  useUpdateTimeEntry,
  useWorkspace,
  useWorkspaceMembers,
  useWorkspaceStatuses,
} from '@fluxter/shared';
import {
  ArrowLeft,
  Check,
  Copy,
  GitBranch,
  GitMerge,
  GitPullRequest,
  Pause,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { MemberAvatar } from '@/components/MemberAvatar';
import { formatActivity, type ActivityEntry } from '@/lib/format-activity';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 5 Task Detail.
 *
 * Direct port of `app/fluxter/project/{taskdetail.css, taskdetail.jsx}`.
 * Linear-style two-pane: 880px-max main column on the left, 320px
 * Properties rail on the right. Sub-bar with back / code / status
 * pill / actions sits between the workspace topbar and the body.
 *
 * Sections (top → bottom):
 *   - Title (auto-resize textarea, 28/600/-0.025em)
 *   - Tags row (multi-select picker)
 *   - Description (TipTap — kept from existing page; framed in
 *     `td-desc-frame`-equivalent border-on-focus)
 *   - Linked tasks (no backend model yet — empty state per Q1)
 *   - Pull requests
 *   - Time tracking (estimate + logged inputs + start/stop timer
 *     wired to TimeEntry mutations + 4px progress bar)
 *   - Activity (segmented All / Comments / Activity tabs; merged
 *     feed of activity entries + comments by timestamp; comment
 *     composer with ⌘+Enter)
 *
 * Right rail: Properties (Status · Priority · Assignee · Due date · Tags)
 *   ─ Estimate · Logged ─ Reporter · Project ─ Created/Updated
 *
 * (No Linked-tasks affordance yet — deferred until a task-relations
 * backend model exists; see docs/audit notes. The sub-bar is back ·
 * code · status pill · spacer · Copy link; the "More" overflow
 * button is hidden until it has a menu to open.)
 *
 * Comment composer rides the shared `<CommentEditor>` (TipTap with
 * mentions + markdown shortcuts for **bold** / *italic* / `code` /
 * lists / links). No visible toolbar — markdown shortcuts cover
 * formatting in the Linear / GitHub style. File attachments are still
 * deferred (no upload pipeline).
 */

/* ─ Lazy-load TipTap once per session — same chunk as NoteEditor ── */
const NoteEditor = lazy(() =>
  import('@/components/NoteEditor').then((m) => ({ default: m.NoteEditor })),
);
/* CommentEditor rides the same TipTap chunk; lazy so a task page that
 * never opens the composer doesn't pay the editor cost. */
const CommentEditor = lazy(() =>
  import('@/components/CommentEditor').then((m) => ({ default: m.CommentEditor })),
);

/* ─ Domain helpers ──────────────────────────────────────────────── */

type Priority = 'urgent' | 'high' | 'medium' | 'low' | 'none';
type StatusCategory = 'todo' | 'in_progress' | 'complete';

interface TaskAttrs {
  title?: string;
  description?: string | null;
  code?: string;
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
  parent_task_id?: string | null;
  tag_ids?: string[];
  priority?: Priority;
  estimate_minutes?: string | number | null;
  logged_minutes?: string | number | null;
  comment_count?: number;
  pull_request_count?: number;
  created_by?: string;
  creator_name?: string | null;
  creator_email?: string | null;
  created_at?: string;
  updated_at?: string;
  project_id?: string;
  project_slug?: string;
  project_name?: string;
}

const PRIORITY_INFO: Record<Priority, { name: string; color: string; glyph: string }> = {
  none:   { name: 'No priority', color: '#94a3b8', glyph: '—' },
  low:    { name: 'Low',         color: '#64748b', glyph: '·' },
  medium: { name: 'Medium',      color: '#3b82f6', glyph: '◔' },
  high:   { name: 'High',        color: '#f59e0b', glyph: '◑' },
  urgent: { name: 'Urgent',      color: '#ef4444', glyph: '◉' },
};

const PRIORITY_ORDER: Priority[] = ['none', 'low', 'medium', 'high', 'urgent'];

const SWIMLANE_LABEL: Record<StatusCategory, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  complete: 'Complete',
};

/* Parse "4h", "1h 30m", "45m", "90" (minutes) → minutes int. */
function parseEstimateInput(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const hm = trimmed.match(/^(\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m)?$/i);
  if (hm) {
    const hours = Number.parseFloat(hm[1] ?? '0');
    const mins = hm[2] ? Number.parseInt(hm[2], 10) : 0;
    return Math.round(hours * 60 + mins);
  }
  const mOnly = trimmed.match(/^(\d+)\s*m$/i);
  if (mOnly) return Number.parseInt(mOnly[1] ?? '0', 10);
  const bare = Number.parseInt(trimmed, 10);
  return Number.isFinite(bare) ? bare : null;
}

function formatMinutes(min: number): string {
  if (min === 0) return '0h';
  const safe = Math.max(0, Math.floor(min));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/* ─ Page ─────────────────────────────────────────────────────────── */

export const TaskDetailPage = () => {
  const { workspaceSlug, taskUlid } = useParams({
    from: '/w/$workspaceSlug/t/$taskUlid',
  });
  const navigate = useNavigate();

  const taskQ = useTask(workspaceSlug, taskUlid);
  const workspaceQ = useWorkspace(workspaceSlug);
  const statusesQ = useWorkspaceStatuses(workspaceSlug);
  const membersQ = useWorkspaceMembers(workspaceSlug);
  const meQ = useMe();
  const commentsQ = useTaskComments(workspaceSlug, taskUlid);
  const activityQ = useTaskActivity(workspaceSlug, taskUlid);
  const timeEntriesQ = useTaskTimeEntries(workspaceSlug, taskUlid);
  const prsQ = useTaskPullRequests(workspaceSlug, taskUlid);

  const updateTask = useUpdateTask(workspaceSlug, taskUlid);
  const createComment = useCreateTaskComment(workspaceSlug, taskUlid);
  const updateComment = useUpdateComment(workspaceSlug);
  const deleteComment = useDeleteComment(workspaceSlug);
  const createTimeEntry = useCreateTimeEntry(workspaceSlug, taskUlid);

  const task =
    (taskQ.data as { data?: { id: string; attributes?: TaskAttrs } } | undefined)?.data;
  const taskAttrs = task?.attributes ?? {};
  const statusList =
    (statusesQ.data as
      | {
          data?: Array<{
            id: string;
            attributes?: {
              name?: string;
              color?: string;
              category?: StatusCategory;
              is_default?: boolean;
            };
          }>;
        }
      | undefined)?.data ?? [];
  const memberList =
    (membersQ.data as
      | { data?: Array<{ id: string; attributes?: { name?: string; email?: string } }> }
      | undefined)?.data ?? [];

  const reporter = memberList.find((m) => m.id === taskAttrs.created_by);
  const workspaceName =
    (workspaceQ.data as { data?: { attributes?: { name?: string } } } | undefined)?.data
      ?.attributes?.name ?? '';

  const estimateMinutes = useMemo(() => {
    const raw = taskAttrs.estimate_minutes;
    if (raw === null || raw === undefined) return null;
    const n = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }, [taskAttrs.estimate_minutes]);

  const loggedMinutes = useMemo(() => {
    const raw = taskAttrs.logged_minutes;
    if (raw === null || raw === undefined) return 0;
    const n = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  }, [taskAttrs.logged_minutes]);

  const patch = (overrides: Parameters<typeof updateTask.mutate>[0]): void => {
    updateTask.mutate(overrides, {
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not save change.'),
    });
  };

  const handleBack = (): void => {
    if (window.history.length > 1) window.history.back();
    else void navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug } });
  };

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  if (taskQ.isLoading || !task) {
    return (
      <div className="text-foreground-muted -mx-6 px-6 py-12 text-sm">Loading task…</div>
    );
  }

  return (
    /*
      Cancel the workspace shell's `px-6 pb-6` so the detail page
      paints edge-to-edge — the prototype's `.td-root` owns its own
      sub-bar + body padding. `relative` + `isolate` so the radial
      glow and rail backdrop-blur compose without leaking sideways.
    */
    <div className="relative isolate -mx-6 -my-6 flex h-[calc(100%+3rem)] flex-1 flex-col">
      {/* Radial backdrop glow (.v1-board-glow) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
        style={{
          background:
            'radial-gradient(60% 50% at 80% 0%, var(--brand-glow-1), transparent 70%),' +
            'radial-gradient(50% 60% at 10% 30%, var(--brand-glow-2), transparent 60%)',
        }}
      />

      {/* ── Sub-bar (44px) ───────────────────────────────────────── */}
      <div
        className={cn(
          'relative z-[5] flex h-[44px] flex-shrink-0 items-center gap-2.5 px-[22px]',
          'border-b border-[var(--glass-card-border)] bg-background/90 backdrop-blur-md',
        )}
      >
        <button
          type="button"
          onClick={handleBack}
          className={cn(
            'text-foreground-muted hover:bg-accent hover:text-foreground inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2 text-[12px]',
          )}
        >
          <ArrowLeft className="size-3.5" /> Back
        </button>
        <span
          className={cn(
            'text-foreground-muted rounded-[5px] border px-[7px] py-0.5 font-mono text-[11px]',
            'border-border bg-secondary',
          )}
        >
          {taskAttrs.code ?? ''}
        </span>
        <StatusInlinePill
          taskAttrs={taskAttrs}
          statusList={statusList}
          onPick={(statusId) => patch({ status_id: statusId })}
        />
        <div className="flex-1" />
        <SubBarIconButton ariaLabel="Copy link" onClick={() => void handleCopyLink()}>
          <Copy className="size-3.5" />
        </SubBarIconButton>
        {/*
          Prototype showed a `MoreHorizontal` overflow button here; hidden
          until we have a real menu to put behind it (Duplicate task,
          Move to project, Convert to subtask, etc — none of which exist
          on the API yet). Leaving it as a no-op click target felt worse
          than dropping the slot.
        */}
      </div>

      {/* ── Body (two-pane grid) ─────────────────────────────────── */}
      <div
        className={cn(
          'relative z-[1] grid flex-1 overflow-y-auto',
          'grid-cols-[minmax(0,1fr)_320px]',
          '[@media(max-width:1100px)]:grid-cols-[minmax(0,1fr)_280px]',
          '[@media(max-width:880px)]:grid-cols-[minmax(0,1fr)]',
        )}
      >
        {/* Main column */}
        <div
          className={cn(
            'mx-auto w-full min-w-0 max-w-[880px] px-12 pt-9 pb-20',
            '[@media(max-width:1100px)]:px-7 [@media(max-width:1100px)]:pt-7 [@media(max-width:1100px)]:pb-14',
          )}
        >
          <TitleEditor
            value={taskAttrs.title ?? ''}
            onChange={(title) => patch({ title })}
          />

          <div className="mt-3.5 mb-[26px] flex flex-wrap items-center gap-1.5">
            <TagPickerInline
              workspaceSlug={workspaceSlug}
              currentTagIds={taskAttrs.tag_ids ?? []}
              onChange={(tag_ids) => patch({ tag_ids })}
            />
          </div>

          <Section title="Description">
            <DescriptionEditor
              workspaceSlug={workspaceSlug}
              value={taskAttrs.description ?? null}
              onSave={(description) => patch({ description: description ?? null })}
            />
          </Section>

          <Section title="Linked tasks" count={0}>
            <p className="text-foreground-faint m-0 px-1 py-1.5 text-[12.5px]">
              No linked tasks.
            </p>
          </Section>

          <Section title="Pull requests" count={taskAttrs.pull_request_count ?? 0}>
            <PullRequestsList query={prsQ} />
          </Section>

          <Section title="Time tracking">
            <TimeTracker
              workspaceSlug={workspaceSlug}
              estimateMinutes={estimateMinutes}
              loggedMinutes={loggedMinutes}
              timeEntries={timeEntriesQ.data}
              onSaveEstimate={(min) => patch({ estimate_minutes: min })}
              onStartTimer={(startedAt) =>
                createTimeEntry.mutateAsync({ started_at: startedAt })
              }
            />
          </Section>

          <ActivitySection
            workspaceSlug={workspaceSlug}
            commentsQuery={commentsQ}
            activityQuery={activityQ}
            members={memberList}
            meQuery={meQ}
            onAddComment={(body) => createComment.mutateAsync({ body })}
            onEditComment={(commentId, body) =>
              updateComment.mutateAsync({ commentId, body })
            }
            onDeleteComment={(commentId) => deleteComment.mutateAsync(commentId)}
          />
        </div>

        {/* Right rail */}
        <aside
          className={cn(
            'border-l border-[var(--glass-card-border)] px-[22px] pt-7 pb-[60px]',
            'bg-[oklch(1_0_0/0.3)] backdrop-blur-[8px]',
            'dark:bg-[oklch(0.18_0.01_250/0.4)]',
            '[@media(max-width:880px)]:border-t [@media(max-width:880px)]:border-l-0 [@media(max-width:880px)]:px-6',
          )}
        >
          <h4 className="text-foreground-muted m-0 mb-3.5 text-[11px] font-semibold tracking-[0.06em] uppercase">
            Properties
          </h4>

          <RailRow label="Status">
            <StatusPicker
              taskAttrs={taskAttrs}
              statusList={statusList}
              onPick={(id) => patch({ status_id: id })}
            />
          </RailRow>
          <RailRow label="Priority">
            <PriorityPicker
              priority={taskAttrs.priority ?? 'none'}
              onPick={(p) => patch({ priority: p })}
            />
          </RailRow>
          <RailRow label="Assignee">
            <AssigneePicker
              currentAssigneeId={taskAttrs.assignee_id ?? null}
              members={memberList}
              onPick={(id) => patch({ assignee_id: id })}
            />
          </RailRow>
          <RailRow label="Due date">
            <DueDateEditor
              dueAt={taskAttrs.due_at ?? null}
              onChange={(dueAt) => patch({ due_at: dueAt })}
            />
          </RailRow>
          <RailRow label="Tags">
            <RailTagPickerInline
              workspaceSlug={workspaceSlug}
              currentTagIds={taskAttrs.tag_ids ?? []}
              onChange={(tag_ids) => patch({ tag_ids })}
            />
          </RailRow>

          <RailDivider />

          <RailRow label="Estimate">
            <EstimateInput
              estimateMinutes={estimateMinutes}
              onSave={(min) => patch({ estimate_minutes: min })}
            />
          </RailRow>
          <RailRow label="Logged">
            <span className="text-foreground-muted font-mono text-[12px]">
              {formatMinutes(loggedMinutes)}
              {estimateMinutes !== null ? (
                <span className="text-foreground-faint"> / {formatMinutes(estimateMinutes)}</span>
              ) : null}
            </span>
          </RailRow>

          <RailDivider />

          <RailRow label="Reporter">
            {reporter ? (
              <span className="inline-flex items-center gap-1.5">
                <MemberAvatar
                  id={reporter.id}
                  name={reporter.attributes?.name}
                  email={reporter.attributes?.email}
                  size="xs"
                  className="!h-[18px] !w-[18px] !text-[8px]"
                />
                <span className="text-foreground text-[12.5px]">
                  {reporter.attributes?.name ?? reporter.attributes?.email ?? '—'}
                </span>
              </span>
            ) : (
              <span className="text-foreground-faint">—</span>
            )}
          </RailRow>
          <RailRow label="Project">
            <span className="text-foreground-muted text-[12.5px]">
              {workspaceName ? `${workspaceName} · ` : ''}
              {taskAttrs.project_name ?? ''}
            </span>
          </RailRow>

          <RailDivider />

          <div className="text-foreground-faint flex flex-col gap-1 text-[11px]">
            <div className="flex items-center justify-between">
              <span>Created</span>
              <span>{formatRelativeTime(taskAttrs.created_at) || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Updated</span>
              <span>{formatRelativeTime(taskAttrs.updated_at) || '—'}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

/* ─ Sub-bar bits ──────────────────────────────────────────────────── */

const SubBarIconButton = ({
  children,
  ariaLabel,
  onClick,
}: {
  children: ReactNode;
  ariaLabel: string;
  onClick?: () => void;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    title={ariaLabel}
    onClick={onClick}
    className={cn(
      'text-foreground-muted hover:bg-accent hover:text-foreground inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent',
    )}
  >
    {children}
  </button>
);

const StatusInlinePill = ({
  taskAttrs,
  statusList,
  onPick,
}: {
  taskAttrs: TaskAttrs;
  statusList: Array<{
    id: string;
    attributes?: { name?: string; color?: string; category?: StatusCategory };
  }>;
  onPick: (statusId: string) => void;
}) => {
  const status = statusList.find((s) => s.id === taskAttrs.status_id);
  if (!status) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'text-foreground inline-flex h-[22px] cursor-pointer items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium',
            'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
          )}
        >
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: status.attributes?.color ?? '#94a3b8' }}
          />
          {status.attributes?.name ?? '—'}
        </button>
      </PopoverTrigger>
      <StatusPickerContent
        currentStatusId={taskAttrs.status_id ?? ''}
        statuses={statusList}
        onPick={onPick}
      />
    </Popover>
  );
};

/* ─ Section header ────────────────────────────────────────────────── */

const Section = ({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <section className="mb-8">
    <div className="mb-3 flex items-center gap-2">
      <h3 className="text-foreground-muted m-0 text-[11px] font-semibold tracking-[0.06em] uppercase">
        {title}
      </h3>
      {typeof count === 'number' ? (
        <span
          className={cn(
            'text-foreground-faint rounded-full px-1.5 py-px text-[11px] tabular-nums',
            'bg-secondary',
          )}
        >
          {count}
        </span>
      ) : null}
      {action ? <div className="ml-auto">{action}</div> : null}
    </div>
    {children}
  </section>
);

/* ─ Title editor (autosize textarea) ─────────────────────────────── */

const TitleEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) => {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [draft]);

  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    else if (!trimmed) setDraft(value);
  };

  return (
    <textarea
      ref={ref}
      value={draft}
      rows={1}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === 'Escape') {
          setDraft(value);
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      placeholder="Task title…"
      className={cn(
        'mb-1 block w-full resize-none border-0 bg-transparent p-1 text-pretty outline-0',
        'text-foreground text-[28px] leading-[1.2] font-semibold tracking-[-0.025em]',
        'focus:-mx-1.5 focus:rounded focus:bg-[oklch(0.74_0.16_50/0.04)] focus:px-1.5',
        'dark:focus:bg-[oklch(0.74_0.16_50/0.08)]',
      )}
    />
  );
};

/* ─ Description (TipTap kept; td-desc-frame border on focus) ──────── */

const DescriptionEditor = ({
  workspaceSlug,
  value,
  onSave,
}: {
  workspaceSlug: string;
  value: string | null;
  onSave: (next: string | null) => void;
}) => (
  <div
    className={cn(
      'rounded-lg border border-transparent px-3 transition-colors duration-100',
      'focus-within:border-[var(--glass-card-border)] focus-within:bg-[var(--glass-card-bg)]',
    )}
  >
    <Suspense
      fallback={
        <div className="text-foreground-faint min-h-[120px] py-2 text-[14.5px] italic">
          Loading editor…
        </div>
      }
    >
      <NoteEditor
        workspaceSlug={workspaceSlug}
        content={value ?? ''}
        placeholder="Add a description…"
        onBlur={(html) => onSave(html || null)}
        // Hide the formatting toolbar until the user clicks into the
        // editor — matches prototype `td-desc-toolbar.is-hidden`.
        toolbarMode="on-focus"
        // Single-line minimum so empty descriptions don't reserve a
        // huge whitespace block; TipTap's contenteditable still grows
        // freely with content.
        minHeight="1.5rem"
      />
    </Suspense>
  </div>
);

/* ─ Pull requests ─────────────────────────────────────────────────── */

interface PullRequestAttrs {
  github_repo_owner?: string;
  github_repo_name?: string;
  pr_number?: number;
  pr_title?: string;
  pr_state?: 'open' | 'draft' | 'closed' | 'merged';
  pr_url?: string;
  pr_author_login?: string | null;
}

const PR_STATE_TONE: Record<NonNullable<PullRequestAttrs['pr_state']>, string> = {
  open:   'bg-[oklch(0.95_0.07_145)] text-[oklch(0.45_0.16_145)] dark:bg-[oklch(0.3_0.1_145/0.4)] dark:text-[oklch(0.78_0.18_145)]',
  merged: 'bg-[oklch(0.94_0.06_290)] text-[oklch(0.45_0.18_290)] dark:bg-[oklch(0.3_0.1_290/0.4)] dark:text-[oklch(0.78_0.18_290)]',
  draft:  'bg-[oklch(0.94_0.005_245)] text-foreground-muted dark:bg-[oklch(0.3_0.005_245/0.4)]',
  closed: 'bg-[oklch(0.94_0.005_245)] text-foreground-muted dark:bg-[oklch(0.3_0.005_245/0.4)]',
};

const PR_STATE_ICON: Record<NonNullable<PullRequestAttrs['pr_state']>, ReactNode> = {
  open:   <GitPullRequest className="size-3" />,
  merged: <GitMerge className="size-3" />,
  draft:  <GitBranch className="size-3" />,
  closed: <GitPullRequest className="size-3" />,
};

const PullRequestsList = ({
  query,
}: {
  query: { data?: unknown; isLoading: boolean };
}) => {
  const items =
    (query.data as
      | { data?: Array<{ id: string; attributes?: PullRequestAttrs }> }
      | undefined)?.data ?? [];

  if (query.isLoading) {
    return <p className="text-foreground-faint px-1 py-1.5 text-[12.5px]">Loading…</p>;
  }
  if (items.length === 0) {
    return (
      <p className="text-foreground-faint m-0 px-1 py-1.5 text-[12.5px]">
        No linked pull requests.
      </p>
    );
  }
  return (
    <div>
      {items.map((it) => {
        const a = it.attributes ?? {};
        const state = a.pr_state ?? 'open';
        return (
          <a
            key={it.id}
            href={a.pr_url ?? '#'}
            target="_blank"
            rel="noreferrer"
            className={cn(
              'mb-1.5 flex items-center gap-2.5 rounded-[10px] border px-3 py-2.5 no-underline',
              'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
            )}
          >
            <span
              className={cn(
                'inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full text-[12px]',
                PR_STATE_TONE[state],
              )}
            >
              {PR_STATE_ICON[state]}
            </span>
            <span className="text-foreground flex-1 truncate text-[13px]">
              {a.pr_title ?? `PR #${a.pr_number ?? '?'}`}
            </span>
            <span className="text-foreground-faint font-mono text-[11px]">
              #{a.pr_number ?? ''}
            </span>
          </a>
        );
      })}
    </div>
  );
};

/* ─ Time tracker ─────────────────────────────────────────────────── */

interface TimeEntryAttrs {
  task_id?: string;
  user_id?: string;
  started_at?: string;
  ended_at?: string | null;
  duration_minutes?: number | null;
}

const TimeTracker = ({
  workspaceSlug,
  estimateMinutes,
  loggedMinutes,
  timeEntries,
  onSaveEstimate,
  onStartTimer,
}: {
  workspaceSlug: string;
  estimateMinutes: number | null;
  loggedMinutes: number;
  timeEntries: unknown;
  onSaveEstimate: (min: number | null) => void;
  onStartTimer: (startedAt: string) => Promise<unknown>;
}) => {
  const list =
    (timeEntries as
      | { data?: Array<{ id: string; attributes?: TimeEntryAttrs }> }
      | undefined)?.data ?? [];
  const running = list.find((e) => e.attributes?.ended_at == null);

  const updateRunning = useUpdateTimeEntry(workspaceSlug, running?.id ?? '');

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const liveExtraMin = running?.attributes?.started_at
    ? Math.max(0, Math.floor((Date.now() - new Date(running.attributes.started_at).getTime()) / 60000))
    : 0;
  const totalMin = loggedMinutes + liveExtraMin;
  const pct =
    estimateMinutes && estimateMinutes > 0
      ? Math.min(100, (totalMin / estimateMinutes) * 100)
      : 0;
  const isOver = estimateMinutes !== null && totalMin > estimateMinutes && estimateMinutes > 0;

  const [estDraft, setEstDraft] = useState<string>(
    estimateMinutes !== null ? formatMinutes(estimateMinutes) : '',
  );
  useEffect(() => {
    setEstDraft(estimateMinutes !== null ? formatMinutes(estimateMinutes) : '');
  }, [estimateMinutes]);

  const commitEstimate = (): void => {
    const parsed = parseEstimateInput(estDraft);
    if (parsed === estimateMinutes) return;
    onSaveEstimate(parsed);
  };

  const toggle = async (): Promise<void> => {
    try {
      if (running) {
        await updateRunning.mutateAsync({ ended_at: new Date().toISOString() });
      } else {
        await onStartTimer(new Date().toISOString());
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not toggle timer');
    }
  };

  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_1fr_auto] gap-3 rounded-xl border p-3.5',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
      )}
    >
      <div className="flex flex-col gap-1">
        <label className="text-foreground-faint text-[10.5px] font-semibold tracking-[0.06em] uppercase">
          Estimate
        </label>
        <input
          value={estDraft}
          onChange={(e) => setEstDraft(e.target.value)}
          onBlur={commitEstimate}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setEstDraft(estimateMinutes !== null ? formatMinutes(estimateMinutes) : '');
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="—"
          className={cn(
            'text-foreground w-full border-0 bg-transparent p-0 font-mono text-[14px] outline-0',
            'focus:text-[var(--brand-accent)]',
          )}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-foreground-faint text-[10.5px] font-semibold tracking-[0.06em] uppercase">
          {running ? 'Logged · live' : 'Logged'}
        </label>
        <input
          value={formatMinutes(totalMin)}
          readOnly
          className={cn(
            'text-foreground w-full border-0 bg-transparent p-0 font-mono text-[14px] outline-0',
          )}
        />
      </div>
      <button
        type="button"
        onClick={() => void toggle()}
        className={cn(
          'inline-flex h-8 cursor-pointer items-center gap-1.5 self-end rounded-lg border-0 px-3 text-[12.5px] font-medium text-white',
          running ? 'bg-[var(--brand-accent)]' : 'bg-[var(--primary)]',
        )}
      >
        {running ? (
          <>
            <Pause className="size-3.5" /> Stop
          </>
        ) : (
          <>
            <Play className="size-3.5" /> Start
          </>
        )}
      </button>
      <div
        aria-hidden="true"
        className="col-span-3 h-1 overflow-hidden rounded-full bg-[var(--surface-quiet)]"
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-[600ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]',
            isOver ? 'bg-[var(--brand-accent)]' : 'bg-[var(--primary)]',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/* ─ Activity / comments feed ──────────────────────────────────────── */

type FeedMode = 'unified' | 'comments' | 'activity';

interface CommentAttrs {
  user_id?: string;
  user_name?: string | null;
  user_email?: string | null;
  body?: string;
  created_at?: string;
  edited_at?: string | null;
}

const ActivitySection = ({
  workspaceSlug,
  commentsQuery,
  activityQuery,
  members,
  meQuery,
  onAddComment,
  onEditComment,
  onDeleteComment,
}: {
  workspaceSlug: string;
  commentsQuery: { data?: unknown; isLoading: boolean };
  activityQuery: { data?: unknown; isLoading: boolean };
  members: Array<{ id: string; attributes?: { name?: string; email?: string } }>;
  meQuery: { data?: unknown };
  onAddComment: (body: string) => Promise<unknown>;
  onEditComment: (commentId: string, body: string) => Promise<unknown>;
  onDeleteComment: (commentId: string) => Promise<unknown>;
}) => {
  const [mode, setMode] = useState<FeedMode>('unified');
  const me =
    (meQuery.data as
      | { data?: { id?: string; attributes?: { name?: string; email?: string } } }
      | undefined)?.data;

  const comments =
    (commentsQuery.data as
      | { data?: Array<{ id: string; attributes?: CommentAttrs }> }
      | undefined)?.data ?? [];
  const activity =
    (activityQuery.data as { data?: ActivityEntry[] } | undefined)?.data ?? [];

  type FeedItem =
    | {
        kind: 'comment';
        id: string;
        at: string;
        comment: { id: string; attributes?: CommentAttrs };
      }
    | { kind: 'event'; id: string; at: string; entry: ActivityEntry };

  const items: FeedItem[] = useMemo(() => {
    const out: FeedItem[] = [];
    for (const c of comments) {
      out.push({
        kind: 'comment',
        id: 'c:' + c.id,
        at: c.attributes?.created_at ?? '',
        comment: c,
      });
    }
    for (const e of activity) {
      out.push({
        kind: 'event',
        id: 'e:' + e.id,
        at: e.attributes?.created_at ?? '',
        entry: e,
      });
    }
    out.sort((a, b) => b.at.localeCompare(a.at));
    return out;
  }, [comments, activity]);

  const filtered = items.filter((i) =>
    mode === 'unified'
      ? true
      : mode === 'comments'
        ? i.kind === 'comment'
        : i.kind === 'event',
  );

  return (
    <Section
      title="Activity"
      action={
        <div
          role="tablist"
          aria-label="Activity feed mode"
          className={cn(
            'inline-flex items-center gap-0.5 rounded-lg border p-0.5',
            'border-border bg-secondary',
          )}
        >
          {(['unified', 'comments', 'activity'] as const).map((m) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                'h-6 rounded-md border-0 px-2.5 text-[11.5px]',
                mode === m
                  ? 'bg-background text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
                  : 'text-foreground-muted hover:text-foreground bg-transparent',
              )}
            >
              {m === 'unified' ? 'All' : m === 'comments' ? 'Comments' : 'Activity'}
            </button>
          ))}
        </div>
      }
    >
      <div className="flex flex-col gap-1.5">
        {filtered.length === 0 ? (
          <p className="text-foreground-faint m-0 px-1 py-1 text-[12.5px]">
            {mode === 'comments' ? 'No comments yet.' : 'No activity yet.'}
          </p>
        ) : (
          filtered.map((it) =>
            it.kind === 'event' ? (
              <ActivityEventRow key={it.id} entry={it.entry} members={members} />
            ) : (
              <CommentCard
                key={it.id}
                comment={it.comment}
                members={members}
                meId={me?.id ?? null}
                onEdit={(body) => onEditComment(it.comment.id, body)}
                onDelete={() => onDeleteComment(it.comment.id)}
              />
            ),
          )
        )}
      </div>
      <CommentComposer
        workspaceSlug={workspaceSlug}
        me={me}
        onSubmit={(body) => onAddComment(body)}
      />
    </Section>
  );
};

const ActivityEventRow = ({
  entry,
  members,
}: {
  entry: ActivityEntry;
  members: Array<{ id: string; attributes?: { name?: string; email?: string } }>;
}) => {
  const formatted = formatActivity(entry);
  const a = entry.attributes ?? {};
  const actor = members.find((m) => m.id === a.actor_user_id);
  const actorName = a.actor_name ?? actor?.attributes?.name ?? a.actor_email ?? 'Someone';
  return (
    <div className="text-foreground-muted flex items-center gap-2.5 px-1 py-1.5 text-[12px]">
      <span
        className={cn(
          'text-foreground-faint inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full',
          'bg-secondary',
        )}
      >
        ·
      </span>
      <span className="flex-1 leading-[1.5]">
        <strong className="text-foreground font-medium">{actorName}</strong> {formatted.verb}
        {formatted.subject ? <> <span className="text-foreground">{formatted.subject.label}</span></> : null}
        {formatted.detail ?? null}
      </span>
      <span className="text-foreground-faint flex-shrink-0 text-[11px]">
        {formatRelativeTime(a.created_at) || ''}
      </span>
    </div>
  );
};

const CommentCard = ({
  comment,
  members,
  meId,
  onEdit,
  onDelete,
}: {
  comment: { id: string; attributes?: CommentAttrs };
  members: Array<{ id: string; attributes?: { name?: string; email?: string } }>;
  meId: string | null;
  onEdit: (body: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) => {
  const a = comment.attributes ?? {};
  const author = members.find((m) => m.id === a.user_id);
  const authorName = a.user_name ?? author?.attributes?.name ?? a.user_email ?? 'Unknown';
  const isMine = meId !== null && a.user_id === meId;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(a.body ?? '');

  const submitEdit = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === a.body) {
      setEditing(false);
      return;
    }
    try {
      await onEdit(trimmed);
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save comment');
    }
  };

  return (
    <div
      className={cn(
        'group/comment my-1 flex gap-3 rounded-xl border p-3.5',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
      )}
    >
      {author ? (
        <MemberAvatar
          id={author.id}
          name={author.attributes?.name}
          email={author.attributes?.email}
          size="md"
          className="!h-7 !w-7 !text-[10px]"
        />
      ) : (
        <span className="bg-secondary inline-flex h-7 w-7 items-center justify-center rounded-full text-[10px]">
          ?
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-foreground text-[13px] font-medium">{authorName}</span>
          <span className="text-foreground-faint text-[11px]">
            {formatRelativeTime(a.created_at) || ''}
            {a.edited_at ? ' · edited' : ''}
          </span>
          {isMine && !editing ? (
            <span className="ml-auto flex gap-1 opacity-0 transition-opacity group-hover/comment:opacity-100">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-foreground-faint hover:text-foreground border-0 bg-transparent text-[11px]"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void onDelete()}
                className="text-foreground-faint hover:text-destructive border-0 bg-transparent text-[11px]"
              >
                Delete
              </button>
            </span>
          ) : null}
        </div>
        {editing ? (
          <div>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  void submitEdit();
                }
                if (e.key === 'Escape') {
                  setDraft(a.body ?? '');
                  setEditing(false);
                }
              }}
              className={cn(
                'text-foreground min-h-[60px] w-full resize-y rounded-md border border-transparent bg-transparent p-1 text-[13.5px] leading-[1.55] outline-0',
                'focus:border-[var(--glass-card-border)]',
              )}
            />
            <div className="mt-1.5 flex gap-2">
              <button
                type="button"
                onClick={() => void submitEdit()}
                className={cn(
                  'inline-flex h-6 items-center rounded border-0 px-2 text-[11px] font-medium text-white',
                  'bg-[var(--primary)]',
                )}
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(a.body ?? '');
                  setEditing(false);
                }}
                className="text-foreground-muted border-0 bg-transparent text-[11px]"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className={cn(
              'text-foreground m-0 text-pretty text-[13.5px] leading-[1.55]',
              '[&>code]:bg-secondary [&>code]:rounded [&>code]:px-1.5 [&>code]:py-px [&>code]:font-mono [&>code]:text-[0.88em]',
            )}
            dangerouslySetInnerHTML={{ __html: a.body ?? '' }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Strip HTML tags + collapse whitespace to detect "is this comment
 * actually empty". TipTap's empty state is `<p></p>` (or `<p><br></p>`
 * after a Cmd+Enter that didn't fire) which `.trim()` won't catch.
 * Mention chips render as `<span>@bob</span>` — a comment that's only
 * a mention strips to `@bob`, length > 0, so it's correctly treated
 * as non-empty. Same helper pattern as `CommentThread.tsx`.
 */
const stripHtmlForEmptyCheck = (html: string): string =>
  html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

const CommentComposer = ({
  workspaceSlug,
  me,
  onSubmit,
}: {
  workspaceSlug: string;
  me: { id?: string; attributes?: { name?: string; email?: string } } | undefined;
  onSubmit: (body: string) => Promise<unknown>;
}) => {
  // `draft` carries the editor's HTML (mentions, bold, code, etc.) —
  // posted as-is to the comment-create endpoint, which already stores
  // and renders bodies as HTML (CommentCard does
  // `dangerouslySetInnerHTML`).
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  // Bumped on successful submit to force a fresh CommentEditor — the
  // editor's `content` prop is captured at mount time (TipTap's
  // `useEditor` doesn't react to content changes after build), so
  // remounting via `key` is the standard idiom for "clear after send".
  const [editorKey, setEditorKey] = useState(0);

  const isEmpty = stripHtmlForEmptyCheck(draft).length === 0;

  const submit = async (): Promise<void> => {
    if (isEmpty || pending) return;
    setPending(true);
    try {
      await onSubmit(draft);
      setDraft('');
      setEditorKey((k) => k + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not post comment');
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className={cn(
        'mt-4 flex items-start gap-3 rounded-xl border p-3 transition-[border-color,box-shadow] duration-100',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
        'focus-within:border-foreground-faint focus-within:shadow-[0_0_0_3px_oklch(0.74_0.16_50/0.1)]',
      )}
    >
      {me?.id ? (
        <MemberAvatar
          id={me.id}
          name={me.attributes?.name}
          email={me.attributes?.email}
          size="md"
          className="!h-8 !w-8 !text-[11px]"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        {/*
          TipTap editor — markdown shortcuts handle bold / italic /
          code / lists / links inline (no visible toolbar, Linear
          style). `@` opens the workspace-members mention picker.
          ⌘+Enter submits via `onSubmitShortcut`.
        */}
        <Suspense
          fallback={
            <div className="text-foreground-faint min-h-[40px] py-1 text-[12.5px] italic">
              Loading editor…
            </div>
          }
        >
          <CommentEditor
            key={editorKey}
            workspaceSlug={workspaceSlug}
            content=""
            onUpdate={setDraft}
            onSubmitShortcut={() => void submit()}
            placeholder="Leave a comment — use @ to mention"
          />
        </Suspense>
        <div
          className={cn(
            'mt-2.5 flex items-center justify-end border-t border-dashed pt-2.5',
            'border-[var(--glass-card-border)]',
          )}
        >
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isEmpty || pending}
            className={cn(
              'inline-flex h-7 cursor-pointer items-center rounded-[7px] border-0 px-3 text-[12.5px] font-medium text-white',
              'bg-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            {pending ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─ Right rail bits ───────────────────────────────────────────────── */

const RailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[90px_1fr] items-center gap-2.5 py-1.5 text-[12.5px]">
    <span className="text-foreground-faint text-[11.5px]">{label}</span>
    <span className="text-foreground flex min-w-0 flex-wrap items-center gap-1.5">
      {children}
    </span>
  </div>
);

const RailDivider = () => (
  <div className="bg-border my-3.5 h-px opacity-70" aria-hidden="true" />
);

const RailTriggerButton = ({
  children,
  empty,
}: {
  children: ReactNode;
  empty?: boolean;
}) => (
  <button
    type="button"
    className={cn(
      '-ml-2 inline-flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md border border-transparent bg-transparent px-2 py-0.5 text-left text-[12.5px]',
      'hover:bg-[var(--glass-card-bg)] hover:border-[var(--glass-card-border)]',
      empty ? 'text-foreground-faint' : 'text-foreground',
    )}
  >
    {children}
  </button>
);

const StatusPicker = ({
  taskAttrs,
  statusList,
  onPick,
}: {
  taskAttrs: TaskAttrs;
  statusList: Array<{
    id: string;
    attributes?: { name?: string; color?: string; category?: StatusCategory };
  }>;
  onPick: (statusId: string) => void;
}) => {
  const status = statusList.find((s) => s.id === taskAttrs.status_id);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <RailTriggerButton>
          <span
            aria-hidden="true"
            className="h-[9px] w-[9px] rounded-full"
            style={{ background: status?.attributes?.color ?? '#94a3b8' }}
          />
          <span className="truncate">{status?.attributes?.name ?? 'No status'}</span>
        </RailTriggerButton>
      </PopoverTrigger>
      <StatusPickerContent
        currentStatusId={taskAttrs.status_id ?? ''}
        statuses={statusList}
        onPick={onPick}
      />
    </Popover>
  );
};

const StatusPickerContent = ({
  currentStatusId,
  statuses,
  onPick,
}: {
  currentStatusId: string;
  statuses: Array<{
    id: string;
    attributes?: { name?: string; color?: string; category?: StatusCategory };
  }>;
  onPick: (id: string) => void;
}) => {
  const byCat: Record<StatusCategory, typeof statuses> = {
    todo: [],
    in_progress: [],
    complete: [],
  };
  for (const s of statuses) {
    const cat = s.attributes?.category ?? 'todo';
    byCat[cat].push(s);
  }
  return (
    <PopoverContent
      align="start"
      sideOffset={6}
      className={cn(
        'bg-background text-foreground min-w-[220px] rounded-[10px] border p-1.5',
        'border-border shadow-[0_16px_40px_oklch(0_0_0/0.18)] ring-0',
      )}
    >
      {(['todo', 'in_progress', 'complete'] as const).map((cat) => (
        <div key={cat}>
          <div className="text-foreground-faint px-2 pt-1.5 pb-1 text-[10.5px] font-semibold tracking-[0.06em] uppercase">
            {SWIMLANE_LABEL[cat]}
          </div>
          {byCat[cat].map((s) => (
            <PickerItem
              key={s.id}
              selected={s.id === currentStatusId}
              onClick={() => onPick(s.id)}
              icon={
                <span
                  aria-hidden="true"
                  className="h-2 w-2 rounded-full"
                  style={{ background: s.attributes?.color ?? '#94a3b8' }}
                />
              }
            >
              {s.attributes?.name ?? '—'}
            </PickerItem>
          ))}
        </div>
      ))}
    </PopoverContent>
  );
};

const PickerItem = ({
  selected,
  onClick,
  icon,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'text-foreground hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]',
      selected && 'bg-[oklch(0.74_0.16_50/0.1)]',
    )}
  >
    <span className="inline-flex w-3.5 justify-center text-[var(--brand-accent)]">
      {selected ? <Check className="size-3" /> : null}
    </span>
    {icon}
    <span className="flex-1 truncate">{children}</span>
  </button>
);

const PriorityPicker = ({
  priority,
  onPick,
}: {
  priority: Priority;
  onPick: (p: Priority) => void;
}) => {
  const info = PRIORITY_INFO[priority];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <RailTriggerButton empty={priority === 'none'}>
          <span
            aria-hidden="true"
            className="inline-block w-3.5 text-center text-[13px]"
            style={{ color: info.color }}
          >
            {info.glyph}
          </span>
          <span className="truncate">{info.name}</span>
        </RailTriggerButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn(
          'bg-background text-foreground min-w-[180px] rounded-[10px] border p-1.5',
          'border-border shadow-[0_16px_40px_oklch(0_0_0/0.18)] ring-0',
        )}
      >
        {PRIORITY_ORDER.map((p) => (
          <PickerItem
            key={p}
            selected={p === priority}
            onClick={() => onPick(p)}
            icon={
              <span
                aria-hidden="true"
                className="inline-block w-3.5 text-center"
                style={{ color: PRIORITY_INFO[p].color }}
              >
                {PRIORITY_INFO[p].glyph}
              </span>
            }
          >
            {PRIORITY_INFO[p].name}
          </PickerItem>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const AssigneePicker = ({
  currentAssigneeId,
  members,
  onPick,
}: {
  currentAssigneeId: string | null;
  members: Array<{ id: string; attributes?: { name?: string; email?: string } }>;
  onPick: (id: string | null) => void;
}) => {
  const [q, setQ] = useState('');
  const current = currentAssigneeId
    ? members.find((m) => m.id === currentAssigneeId)
    : undefined;
  const filtered = members.filter((m) => {
    if (!q) return true;
    const name = (m.attributes?.name ?? m.attributes?.email ?? '').toLowerCase();
    return name.includes(q.toLowerCase());
  });

  return (
    <Popover onOpenChange={(open) => !open && setQ('')}>
      <PopoverTrigger asChild>
        <RailTriggerButton empty={!current}>
          {current ? (
            <MemberAvatar
              id={current.id}
              name={current.attributes?.name}
              email={current.attributes?.email}
              size="xs"
              className="!h-[18px] !w-[18px] !text-[8px]"
            />
          ) : (
            <span
              aria-hidden="true"
              className="border-foreground-faint inline-block h-[18px] w-[18px] rounded-full border border-dashed"
            />
          )}
          <span className="truncate">
            {current
              ? current.attributes?.name ?? current.attributes?.email ?? 'Unknown'
              : 'Unassigned'}
          </span>
        </RailTriggerButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn(
          'bg-background text-foreground min-w-[220px] rounded-[10px] border p-1.5',
          'border-border shadow-[0_16px_40px_oklch(0_0_0/0.18)] ring-0',
        )}
      >
        <input
          autoFocus
          placeholder="Search people…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={cn(
            'text-foreground mb-1 block w-full border-b border-[var(--glass-card-border)] bg-transparent px-2.5 py-1.5 text-[12.5px] outline-0',
          )}
        />
        <PickerItem
          selected={!currentAssigneeId}
          onClick={() => onPick(null)}
          icon={
            <span
              aria-hidden="true"
              className="border-foreground-faint inline-block h-[18px] w-[18px] rounded-full border border-dashed"
            />
          }
        >
          Unassigned
        </PickerItem>
        {filtered.map((m) => (
          <PickerItem
            key={m.id}
            selected={m.id === currentAssigneeId}
            onClick={() => onPick(m.id)}
            icon={
              <MemberAvatar
                id={m.id}
                name={m.attributes?.name}
                email={m.attributes?.email}
                size="xs"
                className="!h-[18px] !w-[18px] !text-[8px]"
              />
            }
          >
            {m.attributes?.name ?? m.attributes?.email ?? 'Unknown'}
          </PickerItem>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const DueDateEditor = ({
  dueAt,
  onChange,
}: {
  dueAt: string | null;
  onChange: (next: string | null) => void;
}) => {
  const value = dueAt ? new Date(dueAt).toISOString().slice(0, 10) : '';
  return (
    <input
      type="date"
      value={value}
      onChange={(e) =>
        onChange(e.target.value ? new Date(e.target.value).toISOString() : null)
      }
      className={cn(
        'text-foreground w-full rounded border-0 bg-transparent px-1.5 py-0.5 text-[12px] outline-0',
        'hover:bg-[var(--glass-card-bg)]',
      )}
    />
  );
};

/* Inline (under-title) tags row — uses the existing TagPicker. */
const TagPickerInline = ({
  workspaceSlug,
  currentTagIds,
  onChange,
}: {
  workspaceSlug: string;
  currentTagIds: string[];
  onChange: (next: string[]) => void;
}) => {
  const TagPickerLazy = useMemo(
    () =>
      lazy(() => import('@/components/TagPicker').then((m) => ({ default: m.TagPicker }))),
    [],
  );
  return (
    <Suspense
      fallback={<span className="text-foreground-faint text-[12px]">Loading tags…</span>}
    >
      <TagPickerLazy
        workspaceSlug={workspaceSlug}
        selectedIds={currentTagIds}
        onChange={onChange}
      />
    </Suspense>
  );
};

/* Right-rail tag picker: same component, narrower context. */
const RailTagPickerInline = TagPickerInline;

const EstimateInput = ({
  estimateMinutes,
  onSave,
}: {
  estimateMinutes: number | null;
  onSave: (min: number | null) => void;
}) => {
  const [draft, setDraft] = useState<string>(
    estimateMinutes !== null ? formatMinutes(estimateMinutes) : '',
  );
  useEffect(() => {
    setDraft(estimateMinutes !== null ? formatMinutes(estimateMinutes) : '');
  }, [estimateMinutes]);
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const parsed = parseEstimateInput(draft);
        if (parsed === estimateMinutes) return;
        onSave(parsed);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(estimateMinutes !== null ? formatMinutes(estimateMinutes) : '');
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder="—"
      className={cn(
        'text-foreground -ml-2 w-full rounded-md border border-transparent bg-transparent px-2 py-0.5 font-mono text-[12px] outline-0',
        'focus:border-[var(--glass-card-border)]',
      )}
    />
  );
};
